import { Ban, Check } from '../../components/ui/icons';
import { Sheet, Modal } from '../../components/ui/Sheet';
import { Avatar } from '../../components/ui/bits';
import { NationalFlagEmblem } from '../../components/ui/NationalFlagEmblems';
import { teamGradient } from '../../lib/teams';
import { cn } from '../../lib/cn';
import type { SelectOption } from './types';

export function BlockedSheet({
  open,
  blocked,
  busy,
  onClose,
  onUnblock
}: {
  open: boolean;
  blocked: { userId: string; displayName?: string | null; username?: string | null }[];
  busy: boolean;
  onClose: () => void;
  onUnblock: (userId: string) => void;
}) {
  return (
    <Sheet open={open} onClose={onClose} ariaLabel="Blocked accounts" className="flex max-h-[80vh] flex-col">
      <h2 className="px-5 pb-1 pt-2 text-[20px] font-extrabold tracking-[-0.02em] text-ink">Blocked accounts</h2>
      <p className="px-5 pb-2 text-[13px] font-medium text-ink2">
        Blocked players can't add you as a friend or challenge you to duels.
      </p>
      <div className="native-scroll no-scrollbar flex-1 overflow-y-auto px-4 pb-4">
        {blocked.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-2 py-10 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-fill2 text-ink3">
              <Ban width={24} height={24} strokeWidth={2} />
            </span>
            <p className="text-[14px] font-medium text-ink2">You haven't blocked anyone.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {blocked.map((b) => (
              <div key={b.userId} className="flex items-center gap-3 rounded-[16px] p-[11px_12px]">
                <Avatar name={b.displayName ?? 'Player'} tone={0} size={34} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold tracking-[-0.01em] text-ink">
                    {b.displayName ?? 'Player'}
                  </div>
                  {b.username && <div className="truncate text-[12px] font-medium text-ink2">@{b.username}</div>}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onUnblock(b.userId)}
                  className="tap rounded-[10px] border border-black/10 bg-card px-[14px] py-[7px] text-[13px] font-bold text-ink disabled:opacity-50"
                >
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}

export function SelectSheet({
  open,
  title,
  options,
  selectedId,
  onClose,
  onSelect,
  variant = 'team'
}: {
  open: boolean;
  title: string;
  options: SelectOption[];
  selectedId?: string;
  onClose: () => void;
  onSelect: (opt: SelectOption) => void;
  variant?: 'team' | 'league';
}) {
  return (
    <Sheet open={open} onClose={onClose} ariaLabel={title} className="flex max-h-[80vh] flex-col">
      <h2 className="px-5 pb-2 pt-2 text-[20px] font-extrabold tracking-[-0.02em] text-ink">{title}</h2>
      <div className="native-scroll no-scrollbar flex-1 overflow-y-auto px-4 pb-4">
        {options.length === 0 ? (
          <p className="px-2 py-6 text-center text-[14px] font-medium text-ink2">Nothing to choose yet.</p>
        ) : (
          <div className="flex flex-col gap-1">
            {options.map((o) => {
              const active = o.id === selectedId;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onSelect(o)}
                  className={cn(
                    'tap flex items-center gap-3 rounded-[16px] p-[11px_12px] text-left',
                    active ? 'bg-fill' : 'bg-transparent'
                  )}
                >
                  {variant === 'league' ? (
                    <NationalFlagEmblem leagueId={o.id} country={o.country} size="xs" />
                  ) : (
                    <span
                      className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] text-[10px] font-extrabold text-white"
                      style={{ backgroundImage: teamGradient(o.code) }}
                    >
                      {o.code}
                    </span>
                  )}
                  <span className="flex-1 text-[15px] font-semibold tracking-[-0.01em] text-ink">{o.label}</span>
                  {active && <Check width={18} height={18} strokeWidth={3} className="text-accent-ink" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Sheet>
  );
}

export function ConfirmDelete({
  open,
  busy,
  onCancel,
  onConfirm
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open={open} onClose={onCancel} ariaLabel="Delete account?">
      <h2 className="text-[19px] font-extrabold tracking-[-0.02em] text-ink">Delete account?</h2>
      <p className="mt-2 text-[14px] font-medium leading-[1.45] text-ink2">
        This permanently removes your profile, league memberships and history. This can’t be undone.
      </p>
      <div className="mt-5 flex flex-col gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className="tap w-full rounded-full bg-danger py-[14px] text-[15px] font-extrabold text-white disabled:opacity-50"
        >
          {busy ? 'Deleting…' : 'Delete my account'}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="tap w-full rounded-full border border-black/5 bg-white py-[14px] text-[15px] font-bold text-ink"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}
